import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { createPageUrl, uploadFile } from "@/utils";
import { Product } from "@/entities/Product";
import { Creator } from "@/entities/Creator";
import { User } from "@/entities/User";
import { UploadCloud, FileSpreadsheet, PlusCircle, ArrowLeft, Check, X, AlertCircle, Download, Eye, Trash2 } from "lucide-react";
import axios from "axios";

const CATEGORIES = ["Artist (Entertainment)", "Content Creator", "Craftsman (Build To Spec)", "Healer (Remedies; Protocol)", "Manifestor", "Motivator", "Soul Worker (Passion Service)", "Problem Solver", "Visionary"];

const CSV_TEMPLATE = [
  ["name", "description", "price", "original_price", "category", "stock", "tags", "status", "is_featured", "is_new"],
  ["Handmade Candle", "Soy wax candle with lavender scent", "24.99", "29.99", "Home Decor", "50", "candle,handmade,lavender", "active", "false", "true"],
  ["Ceramic Bowl", "Hand-thrown stoneware bowl", "45.00", "", "Home Decor", "12", "ceramic,bowl,handmade", "active", "true", "false"],
];

function downloadTemplate() {
  const csv = CSV_TEMPLATE.map(r => r.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "inventory_template.csv"; a.click();
  URL.revokeObjectURL(url);
}

export default function InventoryUpload() {
  const [tab, setTab] = useState("csv");
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);

  // CSV state
  const [csvFile, setCsvFile] = useState(null);
  const [csvRows, setCsvRows] = useState([]);
  const [csvParsing, setCsvParsing] = useState(false);
  const [csvError, setCsvError] = useState("");
  const [selectedCreatorId, setSelectedCreatorId] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef();

  // Manual state
  const [manual, setManual] = useState({ name: "", description: "", price: "", original_price: "", category: "Artist (Entertainment)", stock: 10, tags: "", status: "active", is_featured: false, is_new: true, creator_id: "", images: [] });
  const [manualSaving, setManualSaving] = useState(false);
  const [manualSuccess, setManualSuccess] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);

  useEffect(() => {
    async function load() {
      const me = await User.me();
      if (me?.role !== "admin") { setUnauthorized(true); setLoading(false); return; }
      const crts = await Creator.list("-total_sales", 100);
      setCreators(crts);
      if (crts.length > 0) setSelectedCreatorId(crts[0].id);
      setLoading(false);
    }
    load();
  }, []);

  // CSV Upload & Parse
  const handleCsvFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvFile(file);
    setCsvError("");
    setCsvRows([]);
    setCsvParsing(true);
    const { url } = await uploadFile(file, { isPublic: false });
    const res = await axios.post(`${"https://api.agentui.ai/proxy"}/documents/extract-csv`, { fileUrl: url }, { headers: { "x-api-key": window.config.apiKey } });
    const { rows } = res.data;
    setCsvRows(rows.map((r, i) => ({ ...r, _id: i, _selected: true })));
    setCsvParsing(false);
  };

  const toggleRow = (id) => setCsvRows(r => r.map(x => x._id === id ? { ...x, _selected: !x._selected } : x));
  const removeRow = (id) => setCsvRows(r => r.filter(x => x._id !== id));

  const handleImport = async () => {
    const creator = creators.find(c => c.id === selectedCreatorId);
    const selected = csvRows.filter(r => r._selected);
    if (!selected.length) return;
    setImporting(true);
    const records = selected.map(r => ({
      name: r.name || "",
      description: r.description || "",
      price: parseFloat(r.price) || 0,
      original_price: r.original_price ? parseFloat(r.original_price) : null,
      category: CATEGORIES.includes(r.category) ? r.category : "Home Decor",
      stock: parseInt(r.stock) || 0,
      tags: r.tags ? r.tags.split(",").map(t => t.trim()) : [],
      status: ["active", "draft", "sold_out"].includes(r.status) ? r.status : "active",
      is_featured: r.is_featured === "true",
      is_new: r.is_new !== "false",
      creator_id: selectedCreatorId,
      creator_name: creator?.name || "In-House",
      images: [],
    }));
    await Product.bulkCreate(records);
    setImporting(false);
    setImportResult({ count: records.length });
    setCsvRows([]);
    setCsvFile(null);
  };

  // Manual upload image
  const handleManualImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImg(true);
    const { url } = await uploadFile(file, { isPublic: true });
    setManual(m => ({ ...m, images: [...m.images, url] }));
    setUploadingImg(false);
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    setManualSaving(true);
    const creator = creators.find(c => c.id === manual.creator_id);
    await Product.create({
      ...manual,
      price: parseFloat(manual.price),
      original_price: manual.original_price ? parseFloat(manual.original_price) : null,
      stock: parseInt(manual.stock),
      tags: manual.tags ? manual.tags.split(",").map(t => t.trim()) : [],
      creator_name: creator?.name || "In-House",
    });
    setManualSaving(false);
    setManualSuccess(true);
    setManual({ name: "", description: "", price: "", original_price: "", category: "Home Decor", stock: 10, tags: "", status: "active", is_featured: false, is_new: true, creator_id: manual.creator_id, images: [] });
    setTimeout(() => setManualSuccess(false), 3000);
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#c8a96e] border-t-transparent rounded-full" /></div>;
  if (unauthorized) return <div className="flex flex-col items-center justify-center min-h-[60vh] text-center"><AlertCircle className="w-12 h-12 text-red-400 mb-4" /><h2 className="font-display text-2xl font-bold mb-2">Admin Access Only</h2></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center gap-3 mb-2">
        <Link to={createPageUrl("Admin")} className="text-gray-400 hover:text-[#c8a96e] transition-colors"><ArrowLeft className="w-4 h-4" /></Link>
        <p className="text-[#c8a96e] text-xs font-semibold uppercase tracking-widest">Admin · Inventory</p>
      </div>
      <h1 className="font-display text-3xl font-bold text-[#1a1a1a] mb-2">Upload Inventory</h1>
      <p className="text-gray-500 text-sm mb-8">Add in-house items via CSV bulk upload or manual entry.</p>

      {/* Tabs */}
      <div className="flex gap-2 mb-8 border-b border-[#e8e4de]">
        {[{ id: "csv", label: "📄 Bulk CSV Upload" }, { id: "manual", label: "✏️ Manual Entry" }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${tab === t.id ? "border-[#c8a96e] text-[#c8a96e]" : "border-transparent text-gray-500 hover:text-[#1a1a1a]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* CSV Tab */}
      {tab === "csv" && (
        <div>
          {importResult ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="font-display text-xl font-bold text-[#1a1a1a] mb-2">Import Successful!</h3>
              <p className="text-gray-600 text-sm mb-6">{importResult.count} product{importResult.count !== 1 ? "s" : ""} added to the store.</p>
              <div className="flex gap-3 justify-center">
                <button onClick={() => setImportResult(null)} className="px-5 py-2 bg-[#1a1a1a] text-white rounded-full text-sm font-medium hover:bg-[#c8a96e] transition-colors">Upload More</button>
                <Link to={createPageUrl("ProductManager")} className="px-5 py-2 border border-[#e8e4de] text-[#1a1a1a] rounded-full text-sm font-medium hover:bg-[#f5f3ef] transition-colors">View Products</Link>
              </div>
            </div>
          ) : (
            <>
              {/* Template Download */}
              <div className="bg-[#faf9f7] border border-[#e8e4de] rounded-xl p-5 mb-6 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-[#1a1a1a] text-sm mb-1">Download CSV Template</p>
                  <p className="text-xs text-gray-500">Use our template to format your inventory correctly before uploading.</p>
                </div>
                <button onClick={downloadTemplate} className="inline-flex items-center gap-2 bg-white border border-[#e8e4de] text-[#1a1a1a] text-xs font-semibold px-4 py-2 rounded-full hover:border-[#c8a96e] transition-colors flex-shrink-0">
                  <Download className="w-3.5 h-3.5" /> Template
                </button>
              </div>

              {/* Drop Zone */}
              {!csvFile ? (
                <label className="block border-2 border-dashed border-[#e8e4de] rounded-xl p-12 text-center cursor-pointer hover:border-[#c8a96e] transition-colors group">
                  <UploadCloud className="w-10 h-10 text-gray-300 group-hover:text-[#c8a96e] mx-auto mb-3 transition-colors" />
                  <p className="font-semibold text-[#1a1a1a] mb-1">Drop your CSV file here</p>
                  <p className="text-xs text-gray-400">or click to browse · .csv files only</p>
                  <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleCsvFile} />
                </label>
              ) : csvParsing ? (
                <div className="border border-[#e8e4de] rounded-xl p-12 text-center">
                  <div className="animate-spin w-8 h-8 border-4 border-[#c8a96e] border-t-transparent rounded-full mx-auto mb-4" />
                  <p className="text-sm text-gray-500">Parsing your CSV...</p>
                </div>
              ) : csvRows.length > 0 ? (
                <div>
                  {/* Assign Creator */}
                  <div className="bg-[#faf9f7] border border-[#e8e4de] rounded-xl p-4 mb-4 flex items-center gap-4">
                    <p className="text-sm font-semibold text-[#1a1a1a] flex-shrink-0">Assign to Creator:</p>
                    <select value={selectedCreatorId} onChange={e => setSelectedCreatorId(e.target.value)} className="flex-1 border border-[#e8e4de] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#c8a96e]">
                      {creators.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      <option value="">In-House (No Creator)</option>
                    </select>
                  </div>

                  {/* Preview Table */}
                  <div className="bg-white border border-[#e8e4de] rounded-xl overflow-hidden mb-4">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#e8e4de] bg-[#faf9f7]">
                      <p className="text-sm font-semibold text-[#1a1a1a]">{csvRows.filter(r => r._selected).length} of {csvRows.length} rows selected</p>
                      <button onClick={() => setCsvFile(null)} className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1"><X className="w-3 h-3" /> Clear</button>
                    </div>
                    <div className="overflow-x-auto max-h-72">
                      <table className="w-full text-xs">
                        <thead className="bg-[#faf9f7] sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left w-8"></th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-500">Name</th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-500">Category</th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-500">Price</th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-500">Stock</th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-500">Status</th>
                            <th className="px-3 py-2 w-8"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#e8e4de]">
                          {csvRows.map(r => (
                            <tr key={r._id} className={`${!r._selected ? "opacity-40" : ""}`}>
                              <td className="px-3 py-2"><input type="checkbox" checked={r._selected} onChange={() => toggleRow(r._id)} className="accent-[#c8a96e]" /></td>
                              <td className="px-3 py-2 font-medium text-[#1a1a1a] max-w-[160px] truncate">{r.name}</td>
                              <td className="px-3 py-2 text-gray-500">{r.category}</td>
                              <td className="px-3 py-2 text-gray-500">${r.price}</td>
                              <td className="px-3 py-2 text-gray-500">{r.stock}</td>
                              <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full font-semibold ${r.status === "active" ? "bg-green-100 text-green-600" : "bg-yellow-100 text-yellow-600"}`}>{r.status}</span></td>
                              <td className="px-3 py-2"><button onClick={() => removeRow(r._id)} className="text-gray-300 hover:text-red-400"><Trash2 className="w-3 h-3" /></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <button onClick={handleImport} disabled={importing || !csvRows.filter(r => r._selected).length} className="w-full bg-[#1a1a1a] hover:bg-[#c8a96e] text-white font-semibold py-3 rounded-full transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50">
                    {importing ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                    {importing ? "Importing..." : `Import ${csvRows.filter(r => r._selected).length} Products`}
                  </button>
                </div>
              ) : null}
              {csvError && <p className="text-red-500 text-sm mt-3">{csvError}</p>}
            </>
          )}
        </div>
      )}

      {/* Manual Tab */}
      {tab === "manual" && (
        <form onSubmit={handleManualSubmit} className="bg-white border border-[#e8e4de] rounded-xl p-6 space-y-5">
          {manualSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2 text-green-700 text-sm">
              <Check className="w-4 h-4" /> Product added successfully!
            </div>
          )}

          {/* Image Upload */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Product Images</label>
            <div className="flex flex-wrap gap-2">
              {manual.images.map((img, i) => (
                <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-[#e8e4de]">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setManual(m => ({ ...m, images: m.images.filter((_, j) => j !== i) }))} className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                    <X className="w-2.5 h-2.5 text-white" />
                  </button>
                </div>
              ))}
              <label className="w-16 h-16 border-2 border-dashed border-[#e8e4de] rounded-lg flex items-center justify-center cursor-pointer hover:border-[#c8a96e] transition-colors">
                {uploadingImg ? <div className="w-4 h-4 border-2 border-[#c8a96e] border-t-transparent rounded-full animate-spin" /> : <PlusCircle className="w-5 h-5 text-gray-300" />}
                <input type="file" accept="image/*" className="hidden" onChange={handleManualImage} disabled={uploadingImg} />
              </label>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Product Name *</label>
            <input required value={manual.name} onChange={e => setManual(m => ({ ...m, name: e.target.value }))} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="Item name" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Description *</label>
            <textarea required value={manual.description} onChange={e => setManual(m => ({ ...m, description: e.target.value }))} rows={3} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e] resize-none" placeholder="Describe the item..." />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Price ($) *</label>
              <input required type="number" min="0" step="0.01" value={manual.price} onChange={e => setManual(m => ({ ...m, price: e.target.value }))} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="0.00" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Original Price ($)</label>
              <input type="number" min="0" step="0.01" value={manual.original_price} onChange={e => setManual(m => ({ ...m, original_price: e.target.value }))} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="Optional" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Category *</label>
              <select value={manual.category} onChange={e => setManual(m => ({ ...m, category: e.target.value }))} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#c8a96e]">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Stock Qty</label>
              <input type="number" min="0" value={manual.stock} onChange={e => setManual(m => ({ ...m, stock: e.target.value }))} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Assign to Creator</label>
            <select value={manual.creator_id} onChange={e => setManual(m => ({ ...m, creator_id: e.target.value }))} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#c8a96e]">
              <option value="">In-House Item (No Creator)</option>
              {creators.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Tags (comma separated)</label>
            <input value={manual.tags} onChange={e => setManual(m => ({ ...m, tags: e.target.value }))} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="handmade, gift, limited" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Status</label>
              <select value={manual.status} onChange={e => setManual(m => ({ ...m, status: e.target.value }))} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#c8a96e]">
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="sold_out">Sold Out</option>
              </select>
            </div>
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={manual.is_featured} onChange={e => setManual(m => ({ ...m, is_featured: e.target.checked }))} className="w-4 h-4 accent-[#c8a96e]" />
              <span className="text-sm text-gray-700">Featured</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={manual.is_new} onChange={e => setManual(m => ({ ...m, is_new: e.target.checked }))} className="w-4 h-4 accent-[#c8a96e]" />
              <span className="text-sm text-gray-700">Mark as New</span>
            </label>
          </div>

          <button type="submit" disabled={manualSaving} className="w-full bg-[#1a1a1a] hover:bg-[#c8a96e] text-white font-semibold py-3 rounded-full transition-colors flex items-center justify-center gap-2 text-sm">
            {manualSaving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <PlusCircle className="w-4 h-4" />}
            {manualSaving ? "Adding..." : "Add to Inventory"}
          </button>
        </form>
      )}
    </div>
  );
}